import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Checkbox, Select, Spin, message } from 'antd'
import PeriodoRangePicker from '../../components/PeriodoRangePicker'
import { usePeriodoCompartilhado } from '../../hooks/usePeriodoCompartilhado'
import { useFiltroCompartilhado } from '../../hooks/useFiltroCompartilhado'
import { CalendarBlank, Clock, Plus, Minus, DownloadSimple } from 'phosphor-react'
import * as XLSX from 'xlsx'
import SideMenu from '../../components/SideMenu'
import MultiFiltro from '../../components/MultiFiltro'
import api from '../../service/api'
import * as S from './styles'

const { Option } = Select

/* ---------- tipos da API ---------- */

type AdministradoraTotal = {
  administradora: string
  total: number
  quantidade: number
  percentual: number
  quantidadeCnpj: number
  empresas: string
}
type TipoVenda = { rotulo: string; total: number; quantidade: number; percentual: number; quantidadeCnpj: number; empresas: string }
type HistoricoMes = { mes: number; nomeMes: string; total: number }
type HistoricoHora = { hora: number; total: number }
type TabelaTipo = { tipoVenda: string; total: number; desconto: number; liquido: number; taxaMedia: number }
type TabelaBandeira = {
  bandeira: string
  total: number
  desconto: number
  liquido: number
  taxaMedia: number
  tipos: TabelaTipo[]
}
type TabelaAdministradora = {
  administradora: string
  total: number
  desconto: number
  liquido: number
  taxaMedia: number
  bandeiras: TabelaBandeira[]
}

type DashboardData = {
  totalVendasBruto: number
  quantidadeVendas: number
  totalDescontoMDR: number
  totalVendasLiquido: number
  ticketMedio: number
  totalAReceber: number
  totalPorAdministradora: AdministradoraTotal[]
  tipoDeVendas: TipoVenda[]
  tipoDeCaptura: TipoVenda[]
  historicoPorMes: HistoricoMes[]
  historicoPorHora: HistoricoHora[]
  tabela: TabelaAdministradora[]
}

type Filtros = {
  empresas: string[]
  empresasCnpj: Record<string, string>
  bandeiras: string[]
  anos: number[]
}

type BarItem = {
  label: string
  raw: string
  amount: number
  text: string
  quantidade: number
  percentual: number
  quantidadeCnpj: number
  empresas: string
}

/* ---------- helpers de formatacao ---------- */

const fmtBRL = (n: number) =>
  (n ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
// moeda sem centavos, com separador de milhares (ex.: "R$ 71.234")
const fmtBRL0 = (n: number) =>
  (n ?? 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
const fmtNum = (n: number) => (n ?? 0).toLocaleString('pt-BR')
const fmtPct = (n: number) => `${(n ?? 0).toFixed(2).replace('.', ',')}%`

// paleta estilo Microsoft
const DONUT_COLORS = [
  '#ffbb00', // amarelo
  '#7cbb00', // verde
  '#00a1f1', // azul
  '#f65314', // vermelho
  '#8661c5', // roxo
  '#00b7c3', // ciano
  '#e3008c', // magenta
  '#00cc6a', // verde agua
]

const DONUT_VERDE = '#7cbb00'
const DONUT_LARANJA = '#ffbb00'
const DONUT_AZUL = '#00a1f1'
const DONUT_VOUCHER = '#e66c37'
const DONUT_OUTROS = '#12239e'

// cor fixa por tipo: parcelado=verde, credito a vista=laranja, debito=azul.
// demais tipos caem na paleta ciclica.
function corDoTipo(rotulo: string, index: number): string {
  const t = (rotulo || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // remove acentos
    .toLowerCase()

  if (t.includes('parcelad')) return DONUT_VERDE
  if (t.includes('vista')) return DONUT_LARANJA
  if (t.includes('debito')) return DONUT_AZUL
  if (t.includes('voucher')) return DONUT_VOUCHER
  if (t.includes('outro')) return DONUT_OUTROS

  return DONUT_COLORS[index % DONUT_COLORS.length]
}

const MESES = [
  { v: 1, l: 'Janeiro' },
  { v: 2, l: 'Fevereiro' },
  { v: 3, l: 'Março' },
  { v: 4, l: 'Abril' },
  { v: 5, l: 'Maio' },
  { v: 6, l: 'Junho' },
  { v: 7, l: 'Julho' },
  { v: 8, l: 'Agosto' },
  { v: 9, l: 'Setembro' },
  { v: 10, l: 'Outubro' },
  { v: 11, l: 'Novembro' },
  { v: 12, l: 'Dezembro' },
]

/* ---------- donut (SVG com arcos, linhas-guia e rótulos) ---------- */

// viewBox largo o suficiente para os rotulos longos (ex.: "CRÉDITO PARCELADO")
// caberem dos dois lados sem serem cortados pela borda do SVG.
const VB_W = 760 // largura do viewBox
const VB_H = 380 // altura do viewBox
const CX = VB_W / 2 // centro horizontal = meio do viewBox (grafico centralizado)
const CY = 185
const RO = 132 // raio externo do anel
const RI = 84 // raio interno do anel

// ponto polar: ângulo em graus, sentido horário a partir do topo
function polar(r: number, deg: number) {
  const a = (deg * Math.PI) / 180
  return { x: CX + r * Math.sin(a), y: CY - r * Math.cos(a) }
}

function arcPath(a1: number, a2: number) {
  // circulo completo (ex.: um unico tipo com 100%): um arco 0->360 e degenerado
  // (ponto inicial = final) e nao desenha nada; desenha o anel inteiro.
  if (a2 - a1 >= 359.999) {
    const oT = polar(RO, a1)
    const oB = polar(RO, a1 + 180)
    const iT = polar(RI, a1)
    const iB = polar(RI, a1 + 180)
    return (
      `M ${oT.x} ${oT.y} A ${RO} ${RO} 0 1 1 ${oB.x} ${oB.y} A ${RO} ${RO} 0 1 1 ${oT.x} ${oT.y} Z ` +
      `M ${iT.x} ${iT.y} A ${RI} ${RI} 0 1 0 ${iB.x} ${iB.y} A ${RI} ${RI} 0 1 0 ${iT.x} ${iT.y} Z`
    )
  }
  const large = a2 - a1 > 180 ? 1 : 0
  const p1 = polar(RO, a1)
  const p2 = polar(RO, a2)
  const p3 = polar(RI, a2)
  const p4 = polar(RI, a1)
  return `M ${p1.x} ${p1.y} A ${RO} ${RO} 0 ${large} 1 ${p2.x} ${p2.y} L ${p3.x} ${p3.y} A ${RI} ${RI} 0 ${large} 0 ${p4.x} ${p4.y} Z`
}

type Seg = {
  label: string
  raw: string
  pct: number
  color: string
  total: number
  quantidade: number
  quantidadeCnpj: number
  empresas: string
  crescimento?: number | null
}

// rotula fatias a partir de 2%; as menores (fracoes de 1% que se amontoam no topo)
// aparecem so no tooltip, evitando a sobreposicao.
const LIMIAR_ROTULO = 2

function Donut({
  segmentos,
  selecionado,
  onSelecionar,
}: {
  segmentos: Seg[]
  selecionado?: string | null
  onSelecionar?: (raw: string) => void
}) {
  const soma = segmentos.reduce((s, t) => s + t.pct, 0) || 1
  const wrapRef = useRef<HTMLDivElement>(null)
  const [tip, setTip] = useState<{ seg: Seg; left: number; top: number } | null>(null)

  const aoMover = (seg: Seg) => (e: React.MouseEvent) => {
    const rect = wrapRef.current?.getBoundingClientRect()
    if (!rect) return
    setTip({ seg, left: e.clientX - rect.left, top: e.clientY - rect.top })
  }

  // pre-calcula angulos e posicoes de cada fatia (para resolver sobreposicao de rotulos)
  let acc = 0
  const segViews = segmentos.map((seg) => {
    const start = (acc / soma) * 360
    acc += seg.pct
    const end = (acc / soma) * 360
    const mid = (start + end) / 2
    const onRing = polar((RO + RI) / 2, mid)
    const elbow = polar(RO + 16, mid)
    const right = elbow.x >= CX
    return { seg, start, end, right, onRing, elbowX: elbow.x, elbowY: elbow.y, labelY: elbow.y }
  })

  return (
    <div
      ref={wrapRef}
      style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center' }}
      onMouseLeave={() => setTip(null)}
    >
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} role="img" aria-label="Tipo de vendas">
        {segViews.map(({ seg, start, end, right, onRing, elbowX, labelY }) => {
          const labelX = right ? elbowX + 20 : elbowX - 20
          const textX = right ? labelX + 3 : labelX - 3
          const anchor = right ? 'start' : 'end'
          const mostraRotulo = seg.pct >= LIMIAR_ROTULO

          return (
            <g key={seg.label}>
              <path
                d={arcPath(start, end)}
                fill={seg.color}
                stroke="#ffffff"
                strokeWidth={1.5}
                opacity={selecionado && seg.raw !== selecionado ? 0.3 : 1}
                style={{ cursor: 'pointer' }}
                onMouseEnter={aoMover(seg)}
                onMouseMove={aoMover(seg)}
                onClick={() => onSelecionar?.(seg.raw)}
              />
              {mostraRotulo && (
                <>
                  <polyline
                    points={`${onRing.x},${onRing.y} ${elbowX},${labelY} ${labelX},${labelY}`}
                    fill="none"
                    stroke="#9aa2b1"
                    strokeWidth="1"
                  />
                  <text x={textX} y={labelY} textAnchor={anchor}>
                    <tspan x={textX} dy="-2" fontSize="14.5" fontWeight={700} fill="#4a4f57">
                      {seg.label}
                    </tspan>
                    <tspan x={textX} dy="17" fontSize="14" fontWeight={600} fill="#6b7280">
                      {fmtPct(seg.pct)}
                    </tspan>
                  </text>
                </>
              )}
            </g>
          )
        })}
      </svg>

      {tip && (
        <div
          style={{
            position: 'absolute',
            left: tip.left + 14,
            top: tip.top + 14,
            pointerEvents: 'none',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
            padding: '10px 14px',
            fontSize: 13,
            color: '#4a4f57',
            minWidth: 250,
            zIndex: 5,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, marginBottom: 4 }}>
            <span style={{ color: '#8a929e' }}>Tipo de Venda</span>
            <b>{tip.seg.label}</b>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, marginBottom: 4 }}>
            <span style={{ color: '#8a929e' }}>Valor Total</span>
            <b>{fmtBRL(tip.seg.total)}</b>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, marginBottom: 4 }}>
            <span style={{ color: '#8a929e' }}>Quantidade Vendas</span>
            <b>{fmtNum(tip.seg.quantidade)}</b>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, marginBottom: 4 }}>
            <span style={{ color: '#8a929e' }}>% Sobre as Vendas</span>
            <b>{fmtPct(tip.seg.pct)}</b>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
            <span style={{ color: '#8a929e' }}>Quantidade CNPJ</span>
            <b>{fmtNum(tip.seg.quantidadeCnpj)}</b>
          </div>
          {tip.seg.empresas && (
            <div style={{ marginTop: 6, borderTop: '1px solid #eef0f3', paddingTop: 6, maxWidth: 320 }}>
              <span style={{ color: '#8a929e' }}>Empresas</span>
              <div style={{ marginTop: 2, color: '#4a4f57', fontWeight: 600 }}>
                {tip.seg.empresas
                  .split(',')
                  .map((e) => e.trim())
                  .filter(Boolean)
                  .map((e) => (
                    <div key={e}>{e}</div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ---------- component ---------- */

const TODOS = 'todos'

const Dashboard: React.FC = () => {
  // acesso do cliente (ex.: /dashboard?grupo=NOME) — usado no /acessar/:id:
  // escopa os dados e as opcoes de Empresa apenas ao grupo do cliente
  const location = useLocation()
  const grupo = new URLSearchParams(location.search).get('grupo') || ''

  const [filtros, setFiltros] = useState<Filtros>({ empresas: [], empresasCnpj: {}, bandeiras: [], anos: [] })
  const [empresas, setEmpresas] = useFiltroCompartilhado<string[]>('painel.empresas', [])
  // Período (RangePicker) substitui os antigos filtros Ano/Mês/Dia.
  const [periodo, setPeriodo] = usePeriodoCompartilhado()
  const [bandeiras, setBandeiras] = useState<string[]>([])

  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [histModo, setHistModo] = useState<'mes' | 'hora'>('mes')
  const [tipoSelecionado, setTipoSelecionado] = useState<string | null>(null)
  const [admSelecionada, setAdmSelecionada] = useState<string | null>(null)
  const [mesSelecionado, setMesSelecionado] = useState<string | null>(null)
  const [horaSelecionada, setHoraSelecionada] = useState<string | null>(null)

  // tooltip das barras de "Total de Vendas"
  const barrasRef = useRef<HTMLDivElement>(null)
  const [barTip, setBarTip] = useState<{ item: BarItem; left: number; top: number } | null>(null)

  // tooltip das barras do histórico (mês/hora)
  const [histTip, setHistTip] = useState<
    { mes: string; valor: number; crescimento: number | null; x: number; y: number } | null
  >(null)

  const aoMoverBarra = (item: BarItem) => (e: React.MouseEvent) => {
    const rect = barrasRef.current?.getBoundingClientRect()
    if (!rect) return
    setBarTip({ item, left: e.clientX - rect.left, top: e.clientY - rect.top })
  }

  const toggle = (nome: string) =>
    setExpanded((prev) => ({ ...prev, [nome]: !prev[nome] }))

  // carrega opções dos filtros (escopadas ao grupo, quando acesso do cliente)
  useEffect(() => {
    api
      .get('/DashboardVendas/get-filtros', { params: grupo ? { grupo } : {} })
      .then((res) => {
        const d = res.data?.data ?? {}
        setFiltros({
          empresas: d.empresas ?? [],
          empresasCnpj: d.empresasCnpj ?? {},
          bandeiras: d.bandeiras ?? [],
          anos: d.anos ?? [],
        })
      })
      .catch(() => setFiltros({ empresas: [], empresasCnpj: {}, bandeiras: [], anos: [] }))
  }, [grupo])

  const carregar = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, string | number> = {}
      if (grupo) params.NomeDoGrupo = grupo
      if (empresas.length) params.Empresa = empresas.join(',')
      if (periodo?.[0]) params.DataInicio = periodo[0].format('YYYY-MM-DD')
      if (periodo?.[1]) params.DataFim = periodo[1].format('YYYY-MM-DD')
      if (bandeiras.length) params.Bandeira = bandeiras.join(',')
      if (tipoSelecionado) params.TipoSelecionado = tipoSelecionado
      if (admSelecionada) params.AdministradoraSelecionada = admSelecionada
      if (mesSelecionado) params.MesSelecionado = mesSelecionado
      if (horaSelecionada) params.HoraSelecionada = horaSelecionada

      const res = await api.get('/DashboardVendas/get-dashboard-vendas', { params })
      setData(res.data?.data ?? null)
    } catch {
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [grupo, empresas, periodo, bandeiras, tipoSelecionado, admSelecionada, mesSelecionado, horaSelecionada])

  useEffect(() => {
    carregar()
  }, [carregar])

  // deriva estruturas da UI a partir da resposta
  const kpis = useMemo(
    () => [
      { title: 'Total Vendas Bruto', value: fmtBRL(data?.totalVendasBruto ?? 0), color: 'green', hint: `${fmtNum(data?.quantidadeVendas ?? 0)} vendas` },
      { title: 'Total Desconto MDR', value: fmtBRL(data?.totalDescontoMDR ?? 0), color: 'red', hint: 'Total da taxas de débito e crédito' },
      { title: 'Total Vendas Líquido', value: fmtBRL(data?.totalVendasLiquido ?? 0), color: 'green', hint: 'Valor líquido depois das taxas' },
      { title: 'Ticket Médio', value: fmtBRL(data?.ticketMedio ?? 0), color: 'dark' },
      { title: 'A Receber', value: fmtBRL(data?.totalAReceber ?? 0), color: 'green', hint: 'Em aberto com previsão a partir de amanhã' },
    ],
    [data],
  )

  const totalVendas = useMemo(
    () =>
      (data?.totalPorAdministradora ?? []).map((v) => ({
        label: v.administradora || 'NÃO CADASTRADA',
        raw: v.administradora || '',
        amount: v.total,
        text: fmtBRL(v.total),
        quantidade: v.quantidade,
        percentual: v.percentual,
        quantidadeCnpj: v.quantidadeCnpj,
        empresas: v.empresas ?? '',
      })),
    [data],
  )

  const donutSegs: Seg[] = useMemo(
    () =>
      (data?.tipoDeVendas ?? []).map((t, i) => ({
        label: t.rotulo || 'NÃO INF.',
        raw: t.rotulo || '',
        pct: t.percentual,
        color: corDoTipo(t.rotulo, i),
        total: t.total,
        quantidade: t.quantidade,
        quantidadeCnpj: t.quantidadeCnpj,
        empresas: t.empresas,
        crescimento: null,
      })),
    [data],
  )

  const tipoLegend = useMemo(
    () =>
      (data?.tipoDeCaptura ?? []).map((t) => ({
        label: t.rotulo || 'Não Inf.',
        pct: fmtPct(t.percentual),
      })),
    [data],
  )

  const historico = useMemo(() => {
    const base =
      histModo === 'hora'
        ? (data?.historicoPorHora ?? []).map((h) => ({
            mes: `${String(h.hora).padStart(2, '0')}h`,
            key: String(h.hora),
            value: h.total,
          }))
        : (data?.historicoPorMes ?? []).map((h) => ({ mes: h.nomeMes, key: String(h.mes), value: h.total }))

    return base.map((b, i) => {
      const anterior = i > 0 ? base[i - 1].value : null
      const crescimento =
        anterior && anterior !== 0 ? ((b.value - anterior) / anterior) * 100 : null
      return { mes: b.mes, key: b.key, value: b.value, text: fmtBRL0(b.value), crescimento }
    })
  }, [data, histModo])

  const tabela = data?.tabela ?? []
  // Total do rodapé somado a partir das linhas da tabela (já cross-filtradas por
  // tipo/administradora), para acompanhar o clique no donut e nas barras.
  const tableTotal = useMemo(() => {
    const bruto = tabela.reduce((s, g) => s + (g.total ?? 0), 0)
    const desc = tabela.reduce((s, g) => s + (g.desconto ?? 0), 0)
    const liquido = tabela.reduce((s, g) => s + (g.liquido ?? 0), 0)
    return {
      nome: 'Total',
      total: fmtBRL(bruto),
      desconto: fmtBRL(desc),
      liquido: fmtBRL(liquido),
      taxa: fmtPct(bruto > 0 ? (desc / bruto) * 100 : 0),
    }
  }, [tabela])

  const maxVenda = Math.max(1, ...totalVendas.map((v) => v.amount))
  const maxHist = Math.max(1, ...historico.map((h) => h.value))

  // Nome do arquivo: {GRUPO}_{TIPO}_{PERÍODO}.xlsx, tudo em maiúsculo e sem caracteres inválidos.
  const nomeExport = (tipo: string) => {
    const g = (grupo || 'TODOS').toString()
    const ini = periodo?.[0]?.format('DD-MM-YYYY')
    const fim = periodo?.[1]?.format('DD-MM-YYYY')
    const per = ini && fim ? `${ini}_A_${fim}` : ini || fim || 'PERIODO'
    return `${g}_${tipo}_${per}.xlsx`.toUpperCase().replace(/[\\/:*?"<>|]/g, '-')
  }

  // download XLSX resumido: uma linha por Empresa x Administradora x Período
  const baixarXlsx = async () => {
    const params: Record<string, string | number> = {}
    if (grupo) params.NomeDoGrupo = grupo
    if (empresas.length) params.Empresa = empresas.join(',')
    if (periodo?.[0]) params.DataInicio = periodo[0].format('YYYY-MM-DD')
    if (periodo?.[1]) params.DataFim = periodo[1].format('YYYY-MM-DD')
    if (bandeiras.length) params.Bandeira = bandeiras.join(',')

    let rows: {
      periodo: string
      empresa: string
      cnpj: string
      administradora: string
      valorBruto: number
      valorDesconto: number
      valorLiquido: number
      taxaMedia: number
    }[] = []
    try {
      const res = await api.get('/DashboardVendas/get-exportacao', { params })
      rows = res.data?.data ?? []
    } catch {
      rows = []
    }

    const MOEDA = '"R$" #,##0.00'
    const PCT = '0.00%'

    // ---------- Aba Detalhado (por Empresa x Administradora) ----------
    const det: (string | number)[][] = [
      ['PERÍODO', 'EMPRESA', 'CNPJ', 'ADMINISTRADORA', 'VALOR BRUTO', 'VALOR DESCONTO', 'VALOR LÍQUIDO', 'TAXA MÉDIA%'],
    ]
    rows.forEach((r) =>
      det.push([
        r.periodo,
        r.empresa,
        r.cnpj,
        r.administradora,
        r.valorBruto,
        r.valorDesconto,
        r.valorLiquido,
        (r.taxaMedia ?? 0) / 100,
      ]),
    )

    const wsDet = XLSX.utils.aoa_to_sheet(det)
    wsDet['!cols'] = [
      { wch: 12 },
      { wch: 22 },
      { wch: 20 },
      { wch: 22 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 12 },
    ]
    for (let i = 1; i < det.length; i++) {
      ;['E', 'F', 'G'].forEach((c) => {
        if (wsDet[`${c}${i + 1}`]) wsDet[`${c}${i + 1}`].z = MOEDA
      })
      if (wsDet[`H${i + 1}`]) wsDet[`H${i + 1}`].z = PCT
    }

    // ---------- Aba Resumo (somado por Administradora) ----------
    const mapa = new Map<string, { bruto: number; desc: number; liq: number }>()
    rows.forEach((r) => {
      const cur = mapa.get(r.administradora) ?? { bruto: 0, desc: 0, liq: 0 }
      cur.bruto += r.valorBruto ?? 0
      cur.desc += r.valorDesconto ?? 0
      cur.liq += r.valorLiquido ?? 0
      mapa.set(r.administradora, cur)
    })
    const res: (string | number)[][] = [
      ['ADMINISTRADORA', 'VALOR BRUTO', 'VALOR DESCONTO', 'VALOR LÍQUIDO', 'TAXA MÉDIA%'],
    ]
    Array.from(mapa.entries())
      .sort((a, b) => b[1].bruto - a[1].bruto)
      .forEach(([adm, v]) => res.push([adm, v.bruto, v.desc, v.liq, v.bruto > 0 ? v.desc / v.bruto : 0]))

    const wsRes = XLSX.utils.aoa_to_sheet(res)
    wsRes['!cols'] = [{ wch: 22 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 12 }]
    for (let i = 1; i < res.length; i++) {
      ;['B', 'C', 'D'].forEach((c) => {
        if (wsRes[`${c}${i + 1}`]) wsRes[`${c}${i + 1}`].z = MOEDA
      })
      if (wsRes[`E${i + 1}`]) wsRes[`E${i + 1}`].z = PCT
    }

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, wsDet, 'Detalhado')
    XLSX.utils.book_append_sheet(wb, wsRes, 'Resumo')
    XLSX.writeFile(wb, nomeExport('consolidado'))
  }

  // XLSX "Detalhado": detalhe das Taxas Cobradas com o MESMO contexto do consolidado
  // (Período, Empresa, CNPJ) + o detalhe da tela (Administradora, Bandeira, Tipo). Vem do backend
  // (ConciliadorVendas), uma linha por Período x Empresa x CNPJ x Administradora x Bandeira x Tipo.
  const baixarXlsxDetalhado = async () => {
    const params: Record<string, string> = {}
    if (grupo) params.NomeDoGrupo = grupo
    if (empresas.length) params.Empresa = empresas.join(',')
    if (periodo?.[0]) params.DataInicio = periodo[0].format('YYYY-MM-DD')
    if (periodo?.[1]) params.DataFim = periodo[1].format('YYYY-MM-DD')
    if (bandeiras.length) params.Bandeira = bandeiras.join(',')

    let rows: {
      periodo: string
      empresa: string
      cnpj: string
      administradora: string
      bandeira: string
      tipoVenda: string
      valorBruto: number
      valorDesconto: number
      valorLiquido: number
      taxaMedia: number
    }[] = []
    try {
      const res = await api.get('/DashboardVendas/get-exportacao-detalhada', { params })
      rows = res.data?.data ?? []
    } catch {
      message.error('Não foi possível gerar o detalhado')
      return
    }

    const MOEDA = '"R$" #,##0.00'
    const PCT = '0.00%'

    const aoa: (string | number)[][] = [
      ['PERÍODO', 'EMPRESA', 'CNPJ', 'ADMINISTRADORA', 'BANDEIRA', 'TIPO', 'VALOR BRUTO', 'VALOR DESCONTO', 'VALOR LÍQUIDO', 'TAXA MÉDIA%'],
    ]
    rows.forEach((r) =>
      aoa.push([
        r.periodo,
        r.empresa,
        r.cnpj,
        r.administradora,
        r.bandeira,
        r.tipoVenda,
        r.valorBruto,
        r.valorDesconto,
        r.valorLiquido,
        (r.taxaMedia ?? 0) / 100,
      ]),
    )

    const ws = XLSX.utils.aoa_to_sheet(aoa)
    ws['!cols'] = [
      { wch: 14 },
      { wch: 22 },
      { wch: 18 },
      { wch: 20 },
      { wch: 18 },
      { wch: 20 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 12 },
    ]
    for (let i = 1; i < aoa.length; i++) {
      ;['G', 'H', 'I'].forEach((c) => {
        if (ws[`${c}${i + 1}`]) ws[`${c}${i + 1}`].z = MOEDA
      })
      if (ws[`J${i + 1}`]) ws[`J${i + 1}`].z = PCT
    }

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Detalhado')
    XLSX.writeFile(wb, nomeExport('detalhado'))
  }

  // Gera o relatorio no backend (mesmos filtros/periodo da tela, dados de ConciliadorVendas) e
  // joga o texto no clipboard pronto pra colar no WhatsApp. Fallback via textarea.
  const copiarWhatsApp = async () => {
    const params: Record<string, string> = {}
    if (grupo) params.NomeDoGrupo = grupo
    if (empresas.length) params.Empresa = empresas.join(',')
    if (periodo?.[0]) params.DataInicio = periodo[0].format('YYYY-MM-DD')
    if (periodo?.[1]) params.DataFim = periodo[1].format('YYYY-MM-DD')
    if (bandeiras.length) params.Bandeira = bandeiras.join(',')

    let texto = ''
    try {
      const res = await api.get('/DashboardVendas/get-mensagem-whatsapp', { params })
      texto = res.data?.data?.mensagem ?? ''
    } catch {
      message.error('Não foi possível gerar o relatório')
      return
    }
    if (!texto) {
      message.error('Sem dados para o relatório')
      return
    }

    try {
      await navigator.clipboard.writeText(texto)
      message.success('Relatório copiado para o WhatsApp')
    } catch {
      const ta = document.createElement('textarea')
      ta.value = texto
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      try {
        document.execCommand('copy')
        message.success('Relatório copiado para o WhatsApp')
      } catch {
        message.error('Não foi possível copiar')
      }
      document.body.removeChild(ta)
    }
  }

  return (
    <S.Page>
      <SideMenu active="vendas" />

      <S.Main>
        {loading && (
          <S.LoadingOverlay>
            <Spin size="large" tip="Carregando dados..." />
          </S.LoadingOverlay>
        )}

        {/* filtros */}
        <S.FiltersRow>
          <S.Filter>
            <label>Empresa Selecionada</label>
            <Select
              mode="multiple"
              allowClear
              value={empresas}
              onChange={setEmpresas}
              placeholder="Todos"
              maxTagCount="responsive"
              optionLabelProp="label"
              optionFilterProp="label"
              menuItemSelectedIcon={null}
              showArrow
              style={{ width: '100%' }}
            >
              {filtros.empresas.map((e) => {
                const cnpj = filtros.empresasCnpj?.[e] || 'Sem CNPJ'
                return (
                  <Option key={e} value={e} label={e} title={`${e} — CNPJ: ${cnpj}`}>
                    <span title={`CNPJ: ${cnpj}`}>
                      <Checkbox checked={empresas.includes(e)} style={{ marginRight: 8 }} />
                      {e}
                    </span>
                  </Option>
                )
              })}
            </Select>
          </S.Filter>
          <S.Filter style={{ gridColumn: 'span 2' }}>
            <label>Período</label>
            <PeriodoRangePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder={['Início', 'Fim']}
              value={periodo}
              onChange={(dates) => setPeriodo(dates as [any, any])}
              inputReadOnly
            />
          </S.Filter>
          <S.Filter>
            <label>Bandeira Selecionada</label>
            <MultiFiltro
              value={bandeiras}
              onChange={setBandeiras}
              options={filtros.bandeiras.map((b) => ({ value: b, label: b }))}
            />
          </S.Filter>
        </S.FiltersRow>

        {/* KPIs */}
        <S.KpiRow>
          {kpis.map((k) => (
            <S.KpiCard key={k.title}>
              <div className="title">{k.title}</div>
              <div className={`value ${k.color}`}>{loading ? '...' : k.value}</div>
              {k.hint && <div className="hint">{k.hint}</div>}
            </S.KpiCard>
          ))}
        </S.KpiRow>

        {/* Total de Vendas + Tipo de Vendas */}
        <S.Grid>
          <S.Panel>
            <div className="panel-title">Total de Vendas</div>
            {admSelecionada && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  marginBottom: 10,
                  fontSize: 13,
                  color: '#5a6069',
                }}
              >
                <span>
                  Filtrado por administradora: <b>{admSelecionada}</b>
                </span>
                <button
                  type="button"
                  onClick={() => setAdmSelecionada(null)}
                  style={{
                    border: '1px solid #d7dde4',
                    background: '#f7f9fb',
                    borderRadius: 12,
                    padding: '1px 9px',
                    cursor: 'pointer',
                    color: '#5a6069',
                    lineHeight: 1.6,
                  }}
                >
                  limpar ✕
                </button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <S.AxisLabel>Administradora</S.AxisLabel>
              <div
                ref={barrasRef}
                style={{
                  flex: 1,
                  position: 'relative',
                  minHeight: 300,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
                onMouseLeave={() => setBarTip(null)}
              >
                {totalVendas.map((v) => (
                  <S.HBar
                    key={v.label}
                    onMouseEnter={aoMoverBarra(v)}
                    onMouseMove={aoMoverBarra(v)}
                    onClick={() =>
                      setAdmSelecionada((cur) => (cur === v.raw ? null : v.raw || null))
                    }
                    style={{
                      cursor: 'pointer',
                      opacity: admSelecionada && v.raw !== admSelecionada ? 0.4 : 1,
                    }}
                  >
                    <div className="label">{v.label}</div>
                    <div className="track">
                      <div className="fill" style={{ width: `${(v.amount / maxVenda) * 100}%` }} />
                      <div className="amount">{v.text}</div>
                    </div>
                  </S.HBar>
                ))}

                {barTip && (
                  <div
                    style={{
                      position: 'absolute',
                      left: Math.min(barTip.left + 14, (barrasRef.current?.clientWidth ?? 0) - 260),
                      top: barTip.top + 14,
                      pointerEvents: 'none',
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 6,
                      boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
                      padding: '10px 14px',
                      fontSize: 13,
                      color: '#4a4f57',
                      minWidth: 250,
                      zIndex: 5,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, marginBottom: 4 }}>
                      <span style={{ color: '#8a929e' }}>Administradora</span>
                      <b>{barTip.item.label}</b>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, marginBottom: 4 }}>
                      <span style={{ color: '#8a929e' }}>Valor Total</span>
                      <b>{barTip.item.text}</b>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, marginBottom: 4 }}>
                      <span style={{ color: '#8a929e' }}>Quantidade Vendas</span>
                      <b>{fmtNum(barTip.item.quantidade)}</b>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, marginBottom: 4 }}>
                      <span style={{ color: '#8a929e' }}>% Sobre as Vendas</span>
                      <b>{fmtPct(barTip.item.percentual)}</b>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                      <span style={{ color: '#8a929e' }}>Quantidade CNPJ</span>
                      <b>{fmtNum(barTip.item.quantidadeCnpj)}</b>
                    </div>
                    {barTip.item.empresas && (
                      <div style={{ marginTop: 6, borderTop: '1px solid #eef0f3', paddingTop: 6, maxWidth: 320 }}>
                        <span style={{ color: '#8a929e' }}>Empresas</span>
                        <div style={{ marginTop: 2, color: '#4a4f57', fontWeight: 600 }}>
                          {barTip.item.empresas
                            .split(',')
                            .map((e) => e.trim())
                            .filter(Boolean)
                            .map((e) => (
                              <div key={e}>{e}</div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </S.Panel>

          <S.Panel>
            <div className="panel-title">Tipo de Vendas</div>
            {tipoSelecionado && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  marginBottom: 10,
                  fontSize: 13,
                  color: '#5a6069',
                }}
              >
                <span>
                  Filtrado por tipo: <b>{tipoSelecionado}</b>
                </span>
                <button
                  type="button"
                  onClick={() => setTipoSelecionado(null)}
                  style={{
                    border: '1px solid #d7dde4',
                    background: '#f7f9fb',
                    borderRadius: 12,
                    padding: '1px 9px',
                    cursor: 'pointer',
                    color: '#5a6069',
                    lineHeight: 1.6,
                  }}
                >
                  limpar ✕
                </button>
              </div>
            )}
            <S.DonutWrap>
              <div className="donut-svg">
                <Donut
                  segmentos={donutSegs}
                  selecionado={tipoSelecionado}
                  onSelecionar={(raw) =>
                    setTipoSelecionado((cur) => (cur === raw ? null : raw || null))
                  }
                />
              </div>
              <S.DonutLegend>
                {tipoLegend.map((l) => (
                  <div className="item" key={l.label}>
                    {l.label}
                    <b>{l.pct}</b>
                  </div>
                ))}
              </S.DonutLegend>
            </S.DonutWrap>
          </S.Panel>
        </S.Grid>

        {/* Histórico + Tabela */}
        <S.BottomGrid>
          <S.Panel>
            <S.PanelHead>
              <div className="icons">
                <button
                  type="button"
                  className={`icon-btn ${histModo === 'mes' ? 'active' : ''}`}
                  title="Clique para visualizar as vendas por mês"
                  aria-label="Vendas por mês"
                  onClick={() => setHistModo('mes')}
                >
                  <CalendarBlank size={18} weight={histModo === 'mes' ? 'fill' : 'regular'} />
                </button>
                <button
                  type="button"
                  className={`icon-btn ${histModo === 'hora' ? 'active' : ''}`}
                  title="Clique para visualizar as vendas por hora"
                  aria-label="Vendas por hora"
                  onClick={() => setHistModo('hora')}
                >
                  <Clock size={18} weight={histModo === 'hora' ? 'fill' : 'regular'} />
                </button>
              </div>
              <div className="title">
                {histModo === 'hora' ? 'Histórico de Vendas por Hora' : 'Histórico de Vendas por Mês'}
              </div>
            </S.PanelHead>
            {(histModo === 'hora' ? horaSelecionada : mesSelecionado) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  marginBottom: 10,
                  fontSize: 13,
                  color: '#5a6069',
                }}
              >
                <span>
                  Filtrado por {histModo === 'hora' ? 'hora' : 'mês'}:{' '}
                  <b>
                    {histModo === 'hora'
                      ? `${String(horaSelecionada).padStart(2, '0')}h`
                      : MESES.find((m) => String(m.v) === mesSelecionado)?.l ?? mesSelecionado}
                  </b>
                </span>
                <button
                  type="button"
                  onClick={() =>
                    histModo === 'hora' ? setHoraSelecionada(null) : setMesSelecionado(null)
                  }
                  style={{
                    border: '1px solid #d7dde4',
                    background: '#f7f9fb',
                    borderRadius: 12,
                    padding: '1px 9px',
                    cursor: 'pointer',
                    color: '#5a6069',
                    lineHeight: 1.6,
                  }}
                >
                  limpar ✕
                </button>
              </div>
            )}
            <S.VBars
              className={histModo === 'hora' ? 'vertical' : ''}
              onMouseLeave={() => setHistTip(null)}
            >
              {historico.map((h) => {
                const aoMover = (e: React.MouseEvent) =>
                  setHistTip({
                    mes: h.mes,
                    valor: h.value,
                    crescimento: h.crescimento,
                    x: e.clientX,
                    y: e.clientY,
                  })
                const selKey = histModo === 'hora' ? horaSelecionada : mesSelecionado
                const aoClicar = () => {
                  if (histModo === 'hora') {
                    setHoraSelecionada((cur) => (cur === h.key ? null : h.key))
                  } else {
                    setMesSelecionado((cur) => (cur === h.key ? null : h.key))
                  }
                }
                return (
                  <S.VBar
                    key={h.mes}
                    onMouseEnter={aoMover}
                    onMouseMove={aoMover}
                    onClick={aoClicar}
                    style={{ cursor: 'pointer', opacity: selKey && h.key !== selKey ? 0.4 : 1 }}
                  >
                    <div className="cap">{h.text}</div>
                    <div
                      className="col"
                      style={{ height: `${(h.value / maxHist) * (histModo === 'hora' ? 72 : 88)}%` }}
                    />
                    <div className="xlabel">{h.mes}</div>
                  </S.VBar>
                )
              })}
            </S.VBars>

            {histTip && (
              <div
                style={{
                  position: 'fixed',
                  left: histTip.x + 14,
                  top: histTip.y + 14,
                  pointerEvents: 'none',
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.14)',
                  padding: '10px 14px',
                  fontSize: 13,
                  color: '#4a4f57',
                  minWidth: 250,
                  zIndex: 50,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, marginBottom: 4 }}>
                  <span style={{ color: '#8a929e' }}>{histModo === 'hora' ? 'Hora' : 'Nome do Mês'}</span>
                  <b>{histTip.mes}</b>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20, marginBottom: 4 }}>
                  <span style={{ color: '#8a929e' }}>Valor Total</span>
                  <b>{fmtBRL(histTip.valor)}</b>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                  <span style={{ color: '#8a929e' }}>% Crescimento {histModo === 'hora' ? 'Hora' : 'Mês'}</span>
                  <b>{histTip.crescimento != null ? fmtPct(histTip.crescimento) : '—'}</b>
                </div>
              </div>
            )}
          </S.Panel>

          <S.TablePanel>
            <S.TableTitle>
              Taxas Cobradas
              <Select
                className="table-export"
                placeholder="Exportar"
                value={null}
                size="small"
                dropdownMatchSelectWidth={false}
                suffixIcon={<DownloadSimple size={15} weight="bold" />}
                onChange={(v) => {
                  if (v === 'xlsx') baixarXlsx()
                  else if (v === 'whatsapp') copiarWhatsApp()
                  else if (v === 'detalhado') baixarXlsxDetalhado()
                }}
              >
                <Option value="xlsx">Baixar .xlsx Consolidado</Option>
                <Option value="detalhado">Baixar .xlsx Detalhado</Option>
                <Option value="whatsapp">Copiar para WhatsApp</Option>
              </Select>
            </S.TableTitle>
            <S.HScroll>
              <S.HInner>
                <S.TableScroll>
                  <S.Table>
                    <thead>
                      <tr>
                        <th>Nome Administradora</th>
                    <th>Valor Bruto</th>
                    <th>Valor Desconto</th>
                    <th>Valor Líquido</th>
                    <th>Taxa Média</th>
                  </tr>
                </thead>
                <tbody>
                  {tabela.map((g) => {
                    const open = expanded[g.administradora]
                    return (
                      <Fragment key={g.administradora}>
                        <tr className="group">
                          <td>
                            <span className="toggle" onClick={() => toggle(g.administradora)}>
                              {open ? <Minus size={11} weight="bold" /> : <Plus size={11} weight="bold" />}
                            </span>
                            {g.administradora}
                          </td>
                          <td>{fmtBRL(g.total)}</td>
                          <td>{fmtBRL(g.desconto)}</td>
                          <td>{fmtBRL(g.liquido)}</td>
                          <td>{fmtPct(g.taxaMedia)}</td>
                        </tr>
                        {open &&
                          g.bandeiras.map((c) => {
                            const bkey = `${g.administradora}|${c.bandeira}`
                            const bopen = expanded[bkey]
                            const temTipos = c.tipos && c.tipos.length > 0
                            return (
                              <Fragment key={bkey}>
                                <tr className="child">
                                  <td>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', fontWeight: 600 }}>
                                      {temTipos ? (
                                        <span className="toggle" onClick={() => toggle(bkey)}>
                                          {bopen ? <Minus size={11} weight="bold" /> : <Plus size={11} weight="bold" />}
                                        </span>
                                      ) : (
                                        <span style={{ display: 'inline-block', width: 26 }} />
                                      )}
                                      {c.bandeira}
                                    </span>
                                  </td>
                                  <td>{fmtBRL(c.total)}</td>
                                  <td>{fmtBRL(c.desconto)}</td>
                                  <td>{fmtBRL(c.liquido)}</td>
                                  <td>{fmtPct(c.taxaMedia)}</td>
                                </tr>
                                {bopen &&
                                  c.tipos.map((t) => (
                                    <tr className="child" key={`${bkey}|${t.tipoVenda}`}>
                                      <td style={{ paddingLeft: 64 }}>{t.tipoVenda}</td>
                                      <td>{fmtBRL(t.total)}</td>
                                      <td>{fmtBRL(t.desconto)}</td>
                                      <td>{fmtBRL(t.liquido)}</td>
                                      <td>{fmtPct(t.taxaMedia)}</td>
                                    </tr>
                                  ))}
                              </Fragment>
                            )
                          })}
                      </Fragment>
                    )
                  })}
                </tbody>
              </S.Table>
            </S.TableScroll>

            {/* Total fixo no rodapé do card (rola na horizontal junto com o corpo) */}
            <S.Table>
              <tbody>
                <tr className="total">
                  <td>{tableTotal.nome}</td>
                  <td>{tableTotal.total}</td>
                  <td>{tableTotal.desconto}</td>
                  <td>{tableTotal.liquido}</td>
                  <td>{tableTotal.taxa}</td>
                </tr>
              </tbody>
            </S.Table>
              </S.HInner>
            </S.HScroll>
          </S.TablePanel>
        </S.BottomGrid>
      </S.Main>
    </S.Page>
  )
}

export default Dashboard
